import {
  Add as AddIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  FolderOutlined as FolderIcon,
  InfoOutlined,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Slider,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import axios from "axios";
import React, { useCallback, useEffect, useState } from "react";
import { WorkspaceProject } from "../../types";
import DocumentScopeSelector from "../../components/DocumentScopeSelector";
import ModelSelector from "../../components/ModelSelector";
import ToolIntegrationLogos from "../../components/ToolIntegrationLogos";
import VoiceSelector from "../../components/VoiceSelector";
import {
  DEFAULT_MODEL,
  INTEGRATION_PROVIDER_COMPOSIO,
  REASONING_EFFORT_OPTIONS,
  STRATEGY_FUNCTION_CHAIN,
  STRATEGY_ONE_SHOT,
} from "../../constants";
import { FEATURES } from "../../constants/features";
import { useFeatures } from "../../context/FeatureContext";
import { isFreeUser } from "../../utils/subscriptionUtils";

interface IntegrationTool {
  id: string;
  integration_type: string;
  provider?: string;
}

// Helper to count enabled tools based on feature flags and valid integrations
const countEnabledTools = (
  contextSettings: any,
  smartListEnabled: boolean,
  sequenceEnabled: boolean,
  integrationsEnabled: boolean,
  audienceBuilderEnabled: boolean,
  employeeEnabled: boolean,
  isPaidUser: boolean,
  validIntegrations: IntegrationTool[]
): number => {
  const enabledTools: string[] = [];

  // Only count lists if the Smart List feature is enabled
  if (smartListEnabled && contextSettings.lists) {
    enabledTools.push("lists");
  }
  // Only count sequences if the Sequence feature is enabled
  if (sequenceEnabled && contextSettings.sequences) {
    enabledTools.push("sequences");
  }
  // Only count research task if user is on paid plan
  if (isPaidUser && contextSettings.researchTask) {
    enabledTools.push("researchTask");
  }
  if (contextSettings.documentCreation) {
    enabledTools.push("documentCreation");
  }
  // Only count browser task if integrations feature is enabled
  if (integrationsEnabled && contextSettings.browserTask?.enabled) {
    enabledTools.push("browserTask");
  }
  // Only count audience tools if audience builder feature is enabled
  if (audienceBuilderEnabled && contextSettings.audienceTools) {
    enabledTools.push("audienceTools");
  }
  // Only count delegate task if employee feature is enabled
  if (employeeEnabled && contextSettings.delegateTask) {
    enabledTools.push("delegateTask");
  }
  // Count scheduling tools (no feature flag required)
  if (contextSettings.schedulingTools) {
    enabledTools.push("schedulingTools");
  }
  // Only count MCP and Composio tools if integrations feature is enabled AND they still exist
  if (integrationsEnabled) {
    const validMcpIds = new Set(
      validIntegrations.filter((i) => i.integration_type === "tools").map((i) => i.id)
    );
    const validComposioIds = new Set(
      validIntegrations.filter((i) => i.provider === INTEGRATION_PROVIDER_COMPOSIO).map((i) => i.id)
    );

    const mcpServers = contextSettings.mcpServers || [];
    const composioTools = contextSettings.composioTools || [];

    mcpServers
      .filter((id: string) => validMcpIds.has(id))
      .forEach((server: string) => enabledTools.push(`mcp:${server}`));
    composioTools
      .filter((id: string) => validComposioIds.has(id))
      .forEach((tool: string) => enabledTools.push(`composio:${tool}`));
  }

  return enabledTools.length;
};
import { hostname } from "../../utils";
import { useEmployeeTerminology } from "../../utils/terminology";
import ToolsDialog from "./dialogs/ToolsDialog";

// Info Tooltip Component
const InfoTooltip = ({ title }: { title: string }) => (
  <Tooltip title={title} placement="right">
    <InfoOutlined
      sx={{
        fontSize: 18,
        ml: 0.5,
        color: "text.secondary",
        cursor: "help",
        verticalAlign: "middle",
      }}
    />
  </Tooltip>
);

export default ({
  open,
  onClose,
  contextSettings,
  setContextSettings,
  strategyOptions,
  saveCompanyDefaultSettings,
  saveSettings,
  currentPlan,
  company,
  getToken,
  eventListener,
  currentProject,
  onProjectSelect,
}: {
  open: boolean;
  onClose: () => void;
  contextSettings: any;
  setContextSettings: Function;
  strategyOptions: any;
  saveCompanyDefaultSettings: (e: any) => void;
  saveSettings: (e: any) => void;
  currentPlan: any;
  company: any;
  getToken?: Function;
  eventListener?: Function;
  currentProject?: WorkspaceProject | null;
  onProjectSelect?: (project: WorkspaceProject | null) => void;
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { employee: employeeText } = useEmployeeTerminology();
  const { isFeatureEnabled, isFeatureHidden } = useFeatures();
  const [modelInfo, setModelInfo] = useState<any>({});
  const [modelsLoaded, setModelsLoaded] = useState(false);

  // Check feature availability
  const knowledgeBaseHidden = isFeatureHidden(FEATURES.KNOWLEDGE_BASE);
  const smartListFeatureEnabled = isFeatureEnabled(FEATURES.SMART_LIST);
  const sequenceFeatureEnabled = isFeatureEnabled(FEATURES.SEQUENCE);
  const integrationsFeatureEnabled = isFeatureEnabled(FEATURES.INTEGRATIONS);
  const audienceBuilderFeatureEnabled = isFeatureEnabled(FEATURES.AUDIENCE_BUILDER);
  const employeeFeatureEnabled = isFeatureEnabled(FEATURES.EMPLOYEE);
  const isPaidUser = !isFreeUser(currentPlan);

  // State for Tools dialog
  const [toolsDialogOpen, setToolsDialogOpen] = useState(false);
  const [validIntegrations, setValidIntegrations] = useState<IntegrationTool[]>([]);

  // State for project selection
  const [projects, setProjects] = useState<WorkspaceProject[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectSearch, setProjectSearch] = useState("");
  const [showProjectList, setShowProjectList] = useState(false);
  const [createProjectOpen, setCreateProjectOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [newProjectDescription, setNewProjectDescription] = useState("");
  const [creatingProject, setCreatingProject] = useState(false);

  // State for advanced settings section
  const [advancedExpanded, setAdvancedExpanded] = useState(false);

  // Fetch projects for selection
  const fetchProjects = useCallback(async () => {
    if (!getToken || !company) return;
    setProjectsLoading(true);
    try {
      const token = await getToken();
      const response = await axios.get(`${hostname}/projects`, {
        params: { companyId: company.id, search: projectSearch || undefined, includeShared: true },
        headers: { Authorization: `Bearer ${token}` },
      });
      setProjects(response.data.projects || []);
    } catch (error) {
      console.error("Error fetching projects:", error);
    } finally {
      setProjectsLoading(false);
    }
  }, [getToken, company, projectSearch]);

  useEffect(() => {
    if (showProjectList) {
      fetchProjects();
    }
  }, [showProjectList, fetchProjects]);

  const handleProjectSelect = (project: WorkspaceProject | null) => {
    if (onProjectSelect) {
      onProjectSelect(project);
    }
    setShowProjectList(false);
  };

  const handleCreateProject = async () => {
    if (!newProjectName.trim() || !getToken || !company) return;
    setCreatingProject(true);
    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/projects`,
        {
          companyId: company.id,
          name: newProjectName.trim(),
          description: newProjectDescription.trim() || undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const newProject = response.data;
      handleProjectSelect(newProject);
      setCreateProjectOpen(false);
      setNewProjectName("");
      setNewProjectDescription("");
    } catch (error) {
      console.error("Error creating project:", error);
    } finally {
      setCreatingProject(false);
    }
  };

  // Fetch valid integration tools (MCP + Composio) to filter stale IDs
  useEffect(() => {
    if (open && getToken && company && integrationsFeatureEnabled) {
      const fetchIntegrations = async () => {
        try {
          const token = await getToken();
          const response = await axios.get(
            `${hostname}/integrations/by-type/tools`,
            {
              params: { companyId: company.id, provider: INTEGRATION_PROVIDER_COMPOSIO },
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          const items = response.data.items || [];
          // Only include healthy integrations
          const healthy = items.filter((i: any) => !i.status || i.status === "healthy");
          setValidIntegrations(healthy);

          // Clean up stale integration IDs from contextSettings
          const validMcpIds = new Set(
            healthy.filter((i: any) => i.integration_type === "tools").map((i: any) => i.id)
          );
          const validComposioIds = new Set(
            healthy.filter((i: any) => i.provider === INTEGRATION_PROVIDER_COMPOSIO).map((i: any) => i.id)
          );

          const currentMcpServers = contextSettings.mcpServers || [];
          const currentComposioTools = contextSettings.composioTools || [];

          const filteredMcpServers = currentMcpServers.filter((id: string) => validMcpIds.has(id));
          const filteredComposioTools = currentComposioTools.filter((id: string) => validComposioIds.has(id));

          // Only update if there are stale IDs to remove
          if (
            filteredMcpServers.length !== currentMcpServers.length ||
            filteredComposioTools.length !== currentComposioTools.length
          ) {
            setContextSettings({
              ...contextSettings,
              mcpServers: filteredMcpServers,
              composioTools: filteredComposioTools,
            });
          }
        } catch (error) {
          console.error("Error fetching integration tools:", error);
        }
      };
      fetchIntegrations();
    }
  }, [open, getToken, company, integrationsFeatureEnabled]);

  // Fetch model information to determine if tools are supported
  useEffect(() => {
    const fetchModels = async () => {
      try {
        const params = company?.id ? { companyId: company.id } : {};
        const response = await axios.get(`${hostname}/api/v0/models`, {
          params,
        });
        if (response.data && response.data.models) {
          // Convert array to object keyed by model name for easy lookup
          const modelMap = response.data.models.reduce(
            (acc: any, model: any) => {
              acc[model.name] = model;
              return acc;
            },
            {}
          );
          setModelInfo(modelMap);
        }
      } catch (error) {
        console.error("Error fetching models:", error);
      } finally {
        setModelsLoaded(true);
      }
    };

    fetchModels();
  }, [company?.id]);

  // Check if the current model supports tools
  const currentModelSupportsTools =
    modelInfo[contextSettings.model]?.toolsModel || false;

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog
      open={open && modelsLoaded}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        Settings
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          Sources
          <InfoTooltip title="Configure which external data sources the AI can access when generating responses. These sources provide the AI with information beyond its training data, enabling it to answer questions with current information, your specific documents, and real-time web content." />
        </DialogContentText>
        {!knowledgeBaseHidden && (
          <FormControlLabel
            control={
              <Switch
                onChange={(e) => {
                  setContextSettings({
                    ...contextSettings,
                    documents: e.target.checked,
                  });
                }}
                checked={contextSettings.documents}
              />
            }
            label={
              <>
                Knowledge Base
                <InfoTooltip title="When enabled, the AI can search and reference your uploaded documents, files, and knowledge base content. This allows the AI to answer questions based on your company's specific information, internal documentation, product details, and any materials you've uploaded to the platform." />
              </>
            }
          />
        )}
        <FormControlLabel
          control={
            <Switch
              onChange={(e) => {
                setContextSettings({
                  ...contextSettings,
                  searchEngine: e.target.checked,
                });
              }}
              checked={contextSettings.searchEngine}
            />
          }
          label={
            <>
              Search Engine
              <InfoTooltip title="Enables the AI to perform real-time web searches. This is essential for questions requiring current information such as recent events, latest statistics, or any information that changes frequently." />
            </>
          }
        />
        <FormControlLabel
          control={
            <Switch
              onChange={(e) => {
                setContextSettings({
                  ...contextSettings,
                  websitesJs: e.target.checked,
                });
              }}
              checked={contextSettings.websitesJs}
            />
          }
          label={
            <>
              Websites
              <InfoTooltip title="Allows the AI to directly fetch and analyze content from specific websites you mention in your conversations. When you reference a URL, the AI can visit that page and use that information in its response." />
            </>
          }
        />
        <FormControlLabel
          control={
            <Switch
              onChange={(e) => {
                setContextSettings({
                  ...contextSettings,
                  news: e.target.checked,
                });
              }}
              checked={contextSettings.news}
            />
          }
          label={
            <>
              News & Trends
              <InfoTooltip title="Grants the AI access to current news articles, recent events, and Google Trends data. The AI can find trending topics and related suggestions when you ask about current happenings." />
            </>
          }
        />
        <FormControlLabel
          control={
            <Switch
              onChange={(e) => {
                setContextSettings({
                  ...contextSettings,
                  googleMaps: e.target.checked,
                });
              }}
              checked={contextSettings.googleMaps}
            />
          }
          label={
            <>
              Google Maps
              <InfoTooltip title="Enables the AI to search for local businesses, restaurants, and places on Google Maps. The AI can find business details, reviews, ratings, and contact information for places you mention." />
            </>
          }
        />
        <FormControlLabel
          control={
            <Switch
              onChange={(e) => {
                setContextSettings({
                  ...contextSettings,
                  jobs: e.target.checked,
                });
              }}
              checked={contextSettings.jobs}
            />
          }
          label={
            <>
              Jobs
              <InfoTooltip title="Enables the AI to search for job listings and openings. The AI can find job postings, salary information, and company details for career-related queries." />
            </>
          }
        />

        {/* Tools Section - Opens dialog */}
        <Box
          sx={{
            mt: 2,
            p: 2,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            cursor: "pointer",
            "&:hover": {
              borderColor: "primary.main",
              bgcolor: "action.hover",
            },
          }}
          onClick={() => setToolsDialogOpen(true)}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              alignItems: isMobile ? "flex-start" : "center",
              gap: isMobile ? 1.5 : 1,
            }}
          >
            <ToolIntegrationLogos size="small" />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2">
                Connected Tools
                <InfoTooltip title="Advanced AI capabilities that allow the assistant to perform actions beyond just answering questions. These tools enable the AI to interact with your platform, create content, conduct research, and automate browser-based tasks." />
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Configure research, document creation, browser automation, and
                integrations
              </Typography>
            </Box>
            <Chip
              label={`${countEnabledTools(
                contextSettings,
                smartListFeatureEnabled,
                sequenceFeatureEnabled,
                integrationsFeatureEnabled,
                audienceBuilderFeatureEnabled,
                employeeFeatureEnabled,
                isPaidUser,
                validIntegrations
              )} enabled`}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ alignSelf: isMobile ? "flex-end" : "center" }}
            />
          </Box>
        </Box>

        <DialogContentText style={{ marginTop: 16 }}>
          Memory Recall
          <InfoTooltip title="Configure the AI's memory system to retain and recall information from your conversations. This allows the AI to build context over time, remember your preferences, recall past discussions, and provide more personalized responses. Memory can be configured to work within a single chat or across all your conversations with the AI." />
        </DialogContentText>
        <FormControlLabel
          control={
            <Switch
              onChange={(e: any) => {
                setContextSettings({
                  ...contextSettings,
                  longTermMemories: e.target.checked,
                });
              }}
              checked={contextSettings.longTermMemories}
            />
          }
          label={
            <>
              Long-Term Memories
              <InfoTooltip title="When enabled, the AI can access and recall important information from past conversation threads. Memories are always automatically saved as you chat, but this setting controls whether the AI can pull them in to provide context in responses. For example, if you previously mentioned your company's product names, your role, or specific preferences, the AI will recall and use these details when answering questions. This creates a more personalized experience where you don't have to repeat yourself. You can configure whether memories are scoped to just this chat or shared across all your chats using the Memory Scope setting below." />
            </>
          }
        />
        <FormControlLabel
          control={
            <Switch
              onChange={(e: any) => {
                setContextSettings({
                  ...contextSettings,
                  shortTermMemories: e.target.checked,
                });
              }}
              checked={contextSettings.shortTermMemories}
            />
          }
          label={
            <>
              Short-Term Memories
              <InfoTooltip title="Allows the AI to recall context and details from recent messages in the current conversation. This works like working memory, helping the AI maintain context within a single discussion thread. For instance, if you're having a multi-turn conversation about a specific project, the AI will remember earlier details you mentioned without you needing to repeat them. Short-term memories are automatically managed and focus on the most recent and relevant information from the current conversation session." />
            </>
          }
        />
        <DialogContentText style={{ marginTop: 10 }}>
          System Persona
          <InfoTooltip title="Control the context and persona the AI uses when responding. These settings allow you to customize whether the AI incorporates employee profile information, company details, or custom response guidelines. This shapes how the AI presents itself and what background knowledge it uses in conversations." />
        </DialogContentText>
        <FormControlLabel
          control={
            <Switch
              onChange={(e) => {
                setContextSettings({
                  ...contextSettings,
                  // @ts-ignore
                  employee: e.target.checked,
                });
              }}
              checked={contextSettings.employee}
            />
          }
          label={
            <>
              {`${employeeText} Context`}
              <InfoTooltip
                title={`When enabled, the AI will incorporate the ${employeeText.toLowerCase()}'s profile information, background, expertise, and personality into its responses. This includes the ${employeeText.toLowerCase()}'s name, role, bio, skills, and any other profile details you've configured. This makes the AI respond as if it were the specific ${employeeText.toLowerCase()}, using their voice and expertise. For example, if your ${employeeText.toLowerCase()} is positioned as a sales expert, the AI will respond with that perspective and knowledge base.`}
              />
            </>
          }
        />
        <FormControlLabel
          control={
            <Switch
              onChange={(e) => {
                setContextSettings({
                  ...contextSettings,
                  // @ts-ignore
                  company: e.target.checked,
                });
              }}
              checked={contextSettings.company}
            />
          }
          label={
            <>
              Company Context
              <InfoTooltip title="Includes your company's information in the AI's context, such as company name, description, industry, products/services, and other business details you've configured in your company profile. This helps the AI provide responses that are aligned with your company's brand, values, and offerings. For instance, when answering questions, the AI will reference your specific products, understand your industry context, and maintain consistency with your company's positioning." />
            </>
          }
        />
        <FormControlLabel
          control={
            <Switch
              onChange={(e) => {
                setContextSettings({
                  ...contextSettings,
                  // @ts-ignore
                  responseInstructions: e.target.checked,
                });
              }}
              checked={
                contextSettings.responseInstructions !== undefined
                  ? contextSettings.responseInstructions
                  : true
              }
            />
          }
          label={
            <>
              Response Instructions
              <InfoTooltip title="Applies any custom response instructions you've configured for how the AI should format, structure, and style its responses. This might include tone preferences (formal vs. casual), formatting guidelines (use of bullet points, length constraints), communication style (technical vs. simple), or any other specific instructions about how you want the AI to communicate. These instructions act as ongoing guidelines that shape every response the AI generates." />
            </>
          }
        />

        <ModelSelector
          tools={contextSettings.lists || false}
          currentPlan={currentPlan}
          value={contextSettings.model || DEFAULT_MODEL}
          companyId={company?.id}
          onChange={(e) => {
            // Check if the new model supports tools
            const newModel = e.target.value;
            const newModelSupportsTools =
              modelInfo[newModel]?.toolsModel || false;
            const newStrategy = newModelSupportsTools
              ? STRATEGY_FUNCTION_CHAIN
              : STRATEGY_ONE_SHOT;

            // If new model doesn't support tools, disable all tool-based features
            if (!newModelSupportsTools) {
              setContextSettings({
                ...contextSettings,
                model: newModel,
                lists: false,
                researchTask: false,
                documentCreation: false,
                browserTask: {
                  enabled: false,
                  sessionType: "none",
                  integrationId: null,
                },
                mcpServers: [],
                composioTools: [],
                strategy: newStrategy,
              });
            } else {
              setContextSettings({
                ...contextSettings,
                model: newModel,
                strategy: newStrategy,
              });
            }
          }}
        />
        {!knowledgeBaseHidden && (
          <DocumentScopeSelector
            value={contextSettings.documentScope || "root"}
            onChange={(value, documentPath) => {
              const newSettings = {
                ...contextSettings,
                documentScope: value,
              };
              if (documentPath) {
                newSettings.documentPath = documentPath;
              }
              setContextSettings(newSettings);
            }}
            companyId={company.id}
            companyName={company.name}
            getToken={getToken as () => Promise<string>}
            eventListener={eventListener as (event: any) => void}
            disabled={!contextSettings.documents}
            sx={{ mt: 2.5 }}
            tooltip={
              <InfoTooltip title="Choose which documents or folders the AI can access from your knowledge base. Select 'Document Tags' to access only knowledge base documents that match the tags assigned to the employee, '/' for all documents in the root folder, or select a specific folder to limit the AI's search to only documents within that folder. This helps narrow down results and ensures the AI only references relevant documentation for your queries." />
            }
          />
        )}
        <FormControl fullWidth style={{ marginTop: 20 }}>
          <InputLabel>Long-Term Memory Scope</InputLabel>
          <Select
            label="Long-Term Memory Scope"
            value={contextSettings.memoryScope || "chat"}
            disabled={!contextSettings.longTermMemories}
            onChange={(e: any) =>
              setContextSettings({
                ...contextSettings,
                memoryScope: e.target.value,
              })
            }
          >
            <MenuItem value="chat">Current Chat</MenuItem>
            <MenuItem value="all">All Chats</MenuItem>
          </Select>
        </FormControl>

        {/* Voice Settings - Override employee voice */}
        {getToken && company && (
          <>
            <DialogContentText style={{ marginTop: 16 }}>
              Voice Settings
              <InfoTooltip title="Override the default voice for voice conversations. If set, this voice will be used instead of the employee's default voice when generating spoken responses." />
            </DialogContentText>
            <VoiceSelector
              value={contextSettings.voiceId || null}
              onChange={(voiceId) =>
                setContextSettings({
                  ...contextSettings,
                  voiceId: voiceId || undefined,
                })
              }
              companyId={company.id}
              getToken={getToken as () => Promise<string>}
              label="Voice Override (Optional)"
              helperText="Leave blank to use the employee's default voice"
              showTestButton={true}
              eventListener={eventListener as ((event: any) => void) | undefined}
              sx={{ mt: 1 }}
              valueType="voiceId"
            />
          </>
        )}

        {/* Project Selection - Only shown when onProjectSelect is provided (inside chat) */}
        {onProjectSelect && (
          <>
            <DialogContentText style={{ marginTop: 16 }}>
              Workspace Project
              <Chip label="Beta" size="small" sx={{ ml: 1, height: 20, fontSize: "0.7rem" }} />
              <InfoTooltip title="Attach a workspace project to this chat. The AI will have access to read and write files in the project, making it useful for coding tasks, document collaboration, and project management." />
            </DialogContentText>
            <Box
              sx={{
                mt: 1,
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
              }}
            >
              {currentProject ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <FolderIcon color="primary" />
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {currentProject.name}
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => setShowProjectList(true)}
                  >
                    Change
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => handleProjectSelect(null)}
                  >
                    Detach
                  </Button>
                </Box>
              ) : (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                    No project attached
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<FolderIcon />}
                    onClick={() => setShowProjectList(true)}
                  >
                    Attach Project
                  </Button>
                </Box>
              )}

              {/* Project list (expandable) */}
              {showProjectList && (
                <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid", borderColor: "divider" }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search projects..."
                    value={projectSearch}
                    onChange={(e) => setProjectSearch(e.target.value)}
                    sx={{ mb: 1 }}
                  />
                  {projectsLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : projects.length === 0 ? (
                    <Box sx={{ textAlign: "center", py: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        No projects found
                      </Typography>
                    </Box>
                  ) : (
                    <List dense sx={{ maxHeight: 200, overflow: "auto" }}>
                      {projects.map((project) => (
                        <ListItemButton
                          key={project.id}
                          selected={project.id === currentProject?.id}
                          onClick={() => handleProjectSelect(project)}
                          sx={{ borderRadius: 1 }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <FolderIcon color="primary" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={project.name}
                            secondary={project.description || "No description"}
                            primaryTypographyProps={{ variant: "body2" }}
                            secondaryTypographyProps={{ variant: "caption" }}
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  )}
                  <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                    <Button
                      size="small"
                      onClick={() => setShowProjectList(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => setCreateProjectOpen(true)}
                    >
                      New Project
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>

            {/* Create Project Dialog */}
            <Dialog
              open={createProjectOpen}
              onClose={() => setCreateProjectOpen(false)}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <FolderIcon color="primary" />
                  Create New Project
                </Box>
              </DialogTitle>
              <DialogContent>
                <TextField
                  fullWidth
                  label="Project Name"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  sx={{ mt: 1, mb: 2 }}
                  autoFocus
                />
                <TextField
                  fullWidth
                  label="Description (optional)"
                  value={newProjectDescription}
                  onChange={(e) => setNewProjectDescription(e.target.value)}
                  multiline
                  rows={2}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setCreateProjectOpen(false)}>Cancel</Button>
                <Button
                  variant="contained"
                  onClick={handleCreateProject}
                  disabled={!newProjectName.trim() || creatingProject}
                >
                  {creatingProject ? "Creating..." : "Create"}
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}

        {/* Advanced Settings Section */}
        <Box
          sx={{
            mt: 3,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 2,
              cursor: "pointer",
              "&:hover": { bgcolor: "action.hover" },
            }}
            onClick={() => setAdvancedExpanded(!advancedExpanded)}
          >
            <Typography variant="subtitle2">
              Advanced
              <InfoTooltip title="Fine-tune model behavior with temperature and reasoning effort settings. These settings are optional and most users won't need to change them." />
            </Typography>
            <ExpandMoreIcon
              sx={{
                transform: advancedExpanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s",
              }}
            />
          </Box>
          <Collapse in={advancedExpanded}>
            <Box sx={{ px: 2, pb: 2 }}>
              {/* Temperature Slider */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Temperature
                  <InfoTooltip title="Controls randomness in responses. Lower values (0.0-0.3) produce more focused, deterministic outputs. Higher values (0.7-1.0) produce more creative, varied responses. Default is 0.7." />
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 70 }}>
                    Predictable
                  </Typography>
                  <Slider
                    value={contextSettings.temperature ?? 0.7}
                    onChange={(_, value) =>
                      setContextSettings({
                        ...contextSettings,
                        temperature: value as number,
                      })
                    }
                    min={0}
                    max={1}
                    step={0.05}
                    valueLabelDisplay="auto"
                    sx={{ flex: 1 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 50 }}>
                    Creative
                  </Typography>
                  <Typography variant="body2" sx={{ minWidth: 40, textAlign: "right" }}>
                    {(contextSettings.temperature ?? 0.7).toFixed(2)}
                  </Typography>
                </Box>
              </Box>

              {/* Reasoning Effort Select */}
              <FormControl fullWidth size="small">
                <InputLabel>Reasoning Effort</InputLabel>
                <Select
                  label="Reasoning Effort"
                  value={contextSettings.reasoningEffort || ""}
                  onChange={(e) =>
                    setContextSettings({
                      ...contextSettings,
                      reasoningEffort: e.target.value,
                    })
                  }
                >
                  <MenuItem value="">
                    <em>Default (model decides)</em>
                  </MenuItem>
                  {REASONING_EFFORT_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label} - {option.description}
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  Controls how much reasoning the model uses. Only applies to models that support reasoning tokens.
                </Typography>
              </FormControl>
            </Box>
          </Collapse>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={saveCompanyDefaultSettings}>Save As Default</Button>
        <Button onClick={saveSettings} variant="contained">
          Save
        </Button>
      </DialogActions>

      {/* Tools Dialog */}
      <ToolsDialog
        open={toolsDialogOpen}
        onClose={() => setToolsDialogOpen(false)}
        contextSettings={contextSettings}
        setContextSettings={setContextSettings as (settings: any) => void}
        currentModelSupportsTools={currentModelSupportsTools}
        company={company}
        getToken={getToken as (() => Promise<string>) | undefined}
        eventListener={eventListener}
        currentPlan={currentPlan}
      />
    </Dialog>
  );
};
